import { Loader, Search } from "lucide-react";
import { useCallback, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Option } from "~/utils/types";
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  useComboboxAnchor,
} from "./ui/combobox";

export function ComboBox<Multiple extends boolean = false>({
  onChange,
  options,
  value,
  allowClear,
  multiple,
  renderOption,
  resultsWindowSize = 200,
  filterOnOriginal,
  onInputChange,
  loading,
  fetchingResults,
  tabIndex,
  id,
}: Props<Multiple>) {
  const { t } = useTranslation();
  const anchor = useComboboxAnchor();
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const setAnchor = useCallback(
    (element: HTMLDivElement | null) => {
      anchor.current = element;
      // Keep Base UI's popup inside the surrounding Radix modal's focus/pointer boundary.
      setContainer(element?.closest<HTMLElement>('[role="dialog"]') ?? null);
    },
    [anchor],
  );
  const selectedOptions = (multiple ? value || [] : []) as Option[];
  const selected = multiple ? selectedOptions : (options.find((option) => option.value === value) ?? null);

  return (
    <div className="max-w-[600px]" ref={setAnchor}>
      <Combobox<Option, boolean>
        items={options}
        multiple={multiple}
        value={selected}
        autoHighlight
        isItemEqualToValue={(item, selectedValue) => item.value === selectedValue.value}
        itemToStringLabel={(item) => item.label}
        itemToStringValue={(item) => String(item.value)}
        filter={
          fetchingResults
            ? null
            : filterOnOriginal
              ? (item, query) =>
                  filterOnOriginal.some((key) =>
                    String(item.original?.[key] ?? "")
                      .toLowerCase()
                      .includes(query.toLowerCase()),
                  )
              : undefined
        }
        onInputValueChange={(inputValue, details) => {
          if (details.reason === "input-change" || details.reason === "clear-press") {
            onInputChange?.(inputValue);
          }
        }}
        onValueChange={(next) => {
          onChange((multiple ? next : (next as Option | null)?.value) as ValueType<Multiple>);
        }}
      >
        {multiple ? (
          <ComboboxChips>
            <ComboboxValue>
              {selectedOptions.map((option) => (
                <ComboboxChip key={option.value}>{option.label}</ComboboxChip>
              ))}
            </ComboboxValue>
            <ComboboxChipsInput id={id} tabIndex={tabIndex} />
          </ComboboxChips>
        ) : (
          <ComboboxInput id={id} tabIndex={tabIndex} showClear={allowClear} showTrigger={!loading && !fetchingResults}>
            {loading ? (
              <Loader className="size-4 animate-spin" />
            ) : fetchingResults ? (
              <Search className="size-4" />
            ) : null}
          </ComboboxInput>
        )}
        <ComboboxContent anchor={anchor} container={container ?? undefined}>
          <ComboboxEmpty>{t("noOptions")}</ComboboxEmpty>
          <ComboboxList style={{ maxHeight: `min(${resultsWindowSize}px, var(--available-height))` }}>
            {(option: Option) => (
              <ComboboxItem key={option.value} value={option}>
                {renderOption ? renderOption(option) : option.label}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </div>
  );
}

type ValueType<Multiple extends boolean> = Multiple extends true ? Option[] : string | undefined;

interface Props<Multiple extends boolean = false> {
  value: ValueType<Multiple>;
  onChange: (value: ValueType<Multiple>) => void;
  options: Option[];
  multiple?: Multiple;
  allowClear?: boolean;
  renderOption?: (option: Option) => ReactNode;
  rowHeight?: number;
  resultsWindowSize?: number;
  filterOnOriginal?: string[];
  onInputChange?: (value: string) => void;
  loading?: boolean;
  fetchingResults?: boolean;
  tabIndex?: number;
  id?: string;
}
